import { useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { selectedBorder } from './selectedBorder.js'
import { renderControl, ConfigRows, ToggleRow, useCollapsedSections } from './SettingsSections.jsx'
import { LOOK_SECTIONS, LOOK_INPUTS } from '../../lib/lookNodeSettings.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

/**
 * LookNode — the face graph's one look system: where the character looks
 * (eye contact / looking around), and the head, eyes, upper body and blinks
 * that follow. Plugs into Final Pose's `look` input — it adjusts the finished
 * pose rather than being an animation to blend. lookRuntime.js does the work.
 */

const ACCENT = '#30c0e0'
// Each setting tagged with its section title, for the collapsible list.
const LOOK_PINS = LOOK_SECTIONS.flatMap(s => s.settings.map(p => ({ ...p, section: s.title })))

const STATE_COLORS = {
  'Eye contact (hold)':    '#e0b030',
  'Eye contact (dynamic)': '#40d080',
  'Looking around':        '#50a0e0',
  'Inactive':              '#607080',
}

export function LookNode({ data, selected }) {
  const [collapsed, toggleSection] = useCollapsedSections()
  const [showAdvanced, setShowAdvanced] = useState(false)
  const state = data._lookState ?? 'Inactive'
  const warnings = data._lookWarnings ?? []
  const color = STATE_COLORS[state] ?? '#607080'

  return (
    <div style={{
      background: selected ? '#0f1a24' : '#08141c',
      border: `2px solid ${selected ? selectedBorder('#184858') : '#184858'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 240,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
    }}>
      {/* Title + live state */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }}>👀</span>
        <span
          title="One look system: decides where the character looks — eye contact or looking around — and moves the head, eyes and upper body and blinks to match. Plug its Look output into Final Pose's Look input."
          style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5, cursor: 'help' }}
        >LOOK</span>
        <span
          title="The current state"
          style={{
            marginLeft: 'auto', fontSize: 9, padding: '2px 7px', borderRadius: 9,
            color, border: `1px solid ${color}66`, background: `${color}1a`, whiteSpace: 'nowrap',
          }}
        >● {state}</span>
      </div>

      {warnings.map(w => (
        <div key={w} style={{
          color: '#e0b060', fontSize: 9, background: '#2a2010', border: '1px solid #4a3818',
          borderRadius: 3, padding: '3px 6px', marginBottom: 6, maxWidth: 260,
        }}>⚠ {w}</div>
      ))}

      {/* Inputs — wire a variable, or set them here */}
      {LOOK_INPUTS.map(pin => (
        <div key={pin.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, position: 'relative' }}>
          <Handle
            type="target" position={Position.Left} id={pin.id}
            style={{ background: '#4ecf7e', width: 7, height: 7, top: '50%', transform: 'translateY(-50%)' }}
          />
          <span
            title={pin.description}
            style={{ color: '#90a8b8', fontSize: 10, flex: 1, marginLeft: 4, cursor: 'help' }}
          >{pin.label}</span>
          {renderControl(pin, data[pin.id] ?? pin.default, data.onChange)}
        </div>
      ))}

      {/* Output — into Final Pose's Look input */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '2px 0 6px', position: 'relative' }}>
        <span style={{ color: ACCENT, fontSize: 10, marginRight: 12 }}>Look ▶</span>
        <Handle
          type="source" position={Position.Right} id="look"
          style={{ background: ACCENT, width: 10, height: 10, borderRadius: 2 }}
        />
      </div>

      <div style={{ borderTop: '1px solid #22323e', paddingTop: 6 }}>
        <ConfigRows pins={LOOK_PINS} data={data} collapsed={collapsed} toggle={toggleSection} />
        {/* Kept for settings that move here later; empty for now. */}
        <ToggleRow open={showAdvanced} onToggle={() => setShowAdvanced(v => !v)}
          label="Advanced" count={0}
          title={showAdvanced ? 'Hide the advanced settings' : 'Show the advanced settings'} />
        {showAdvanced && (
          <div style={{ color: '#4a5a68', fontSize: 9, padding: '2px 0 4px' }}>Nothing here yet.</div>
        )}
        {data.onResetToDefaults && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              className={nodeControlClass()}
              data-testid="look-reset-to-defaults"
              title="Put every setting on this node back to its default (asks first; Ctrl+Z undoes it)"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); data.onResetToDefaults() }}
              style={{
                background: 'transparent', border: `1px solid ${ACCENT}55`, color: ACCENT,
                borderRadius: 3, fontSize: 9, fontFamily: 'var(--m-font)',
                padding: '2px 8px', cursor: 'pointer',
              }}
            >reset-to-defaults</button>
          </div>
        )}
      </div>
    </div>
  )
}
